export function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12) return false;

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];

  return day >= 1 && day <= daysInMonth[month - 1]!;
}

export function calendarDateToUtcDate(value: string): Date {
  if (!isCalendarDate(value)) {
    throw new Error(`Invalid calendar date: ${value}`);
  }

  const [year, month, day] = value.split("-").map(Number);
  return new Date(Date.UTC(year!, month! - 1, day!));
}

export function utcDateToCalendarDate(value: Date): string {
  return `${value.getUTCFullYear().toString().padStart(4, "0")}-${(value.getUTCMonth() + 1).toString().padStart(2, "0")}-${value.getUTCDate().toString().padStart(2, "0")}`;
}

export function normalizeCalendarDate(value: string | Date): string {
  return value instanceof Date ? utcDateToCalendarDate(value) : value;
}

export function addCalendarDays(value: string, days: number): string {
  const date = calendarDateToUtcDate(value);
  date.setUTCDate(date.getUTCDate() + days);
  return utcDateToCalendarDate(date);
}

export function compareCalendarDates(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

export function calendarDateInTimeZone(instant: Date, timeZone: string): string {
  if (!Number.isFinite(instant.getTime())) throw new TypeError("instant must be a valid Date");
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const result = `${values["year"]}-${values["month"]}-${values["day"]}`;
  if (!isCalendarDate(result)) throw new RangeError(`Could not determine calendar date in ${timeZone}`);
  return result;
}

/** Return the UTC instant at the start of a calendar day in the requested time zone. */
export function calendarDateStartInTimeZoneUtc(value: string, timeZone: string): Date {
  if (!isCalendarDate(value)) throw new TypeError("value must be a valid calendar date");
  const target = calendarDateToUtcDate(value).getTime();
  let candidate = target;
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  for (let attempt = 0; attempt < 4; attempt++) {
    const parts = formatter.formatToParts(new Date(candidate));
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const localAsUtc = Date.UTC(
      Number(values["year"]), Number(values["month"]) - 1, Number(values["day"]),
      Number(values["hour"]), Number(values["minute"]), Number(values["second"]),
    );
    const corrected = candidate + target - localAsUtc;
    if (corrected === candidate) break;
    candidate = corrected;
  }
  const result = new Date(candidate);
  if (calendarDateInTimeZone(result, timeZone) !== value) {
    throw new RangeError(`Could not resolve the start of ${value} in ${timeZone}`);
  }
  return result;
}
